import React, { useState, useEffect } from 'react';
import { Card, Space, Spin, Typography, Tag, Row, Col, Statistic, Divider, Progress } from 'antd';
import { RobotOutlined, BulbOutlined, RiseOutlined, DollarOutlined, BarChartOutlined } from '@ant-design/icons';
import { formatCurrency } from '../../utils/format';

const { Text } = Typography;

/**
 * AI-Powered Price Recommendation Component
 * Analyzes bidding patterns and provides intelligent bid recommendations
 * @param {Object} props
 * @param {Object} props.lot - The lot object
 * @param {Array} props.bidHistory - Array of bid history for the lot
 * @param {Array} props.lots - Array of all lots for market comparison
 * @param {Object} props.currentUser - Current logged in user
 * @param {number} props.auctionOwnerId - ID of the auction owner
 */
const AIPriceRecommendation = ({ lot, bidHistory, lots, currentUser, auctionOwnerId }) => {
    const [recommendations, setRecommendations] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!lot || lot.status === 'closed' || lot.status === 'cancelled') {
            setRecommendations(null);
            return;
        }

        setLoading(true);
        // Simulate AI analysis (in production, this could call an API)
        setTimeout(() => {
            const analysis = analyzeBiddingPatterns(lot, bidHistory, lots, currentUser, auctionOwnerId);
            setRecommendations(analysis);
            setLoading(false);
        }, 500);
    }, [lot, bidHistory, lots, currentUser, auctionOwnerId]);

    const analyzeBiddingPatterns = (lot, bidHistory, allLots, currentUser, auctionOwnerId) => {
        const currentPrice = parseFloat(lot.current_price || lot.starting_price || 0);
        const startingPrice = parseFloat(lot.starting_price || 0);
        // Only use reserve price in calculations if user is auction owner, otherwise hide it
        const reservePrice = (lot.reserve_price && currentUser?.id === auctionOwnerId) ? parseFloat(lot.reserve_price) : null;
        const increment = parseInt(lot.default_increment) || parseInt(lot.increment) || 5;
        
        // Analyze bid history - use lot.bids_count as fallback if bidHistory is empty
        const bids = Array.isArray(bidHistory) ? bidHistory : [];
        // Use bidHistory length if available, otherwise fall back to lot.bids_count
        // Also check if current_price > starting_price as an indicator of bids
        let bidCount = bids.length;
        if (bidCount === 0) {
            // Try to get bid count from lot data
            if (lot.bids_count !== undefined && lot.bids_count !== null) {
                bidCount = lot.bids_count;
            } else if (currentPrice > startingPrice && startingPrice > 0) {
                // If price has increased, there must be at least 1 bid
                // Estimate based on price difference and increment
                const priceDiff = currentPrice - startingPrice;
                bidCount = Math.max(1, Math.floor(priceDiff / increment));
            }
        }
        
        // Calculate bid velocity (bids per minute if we have time data)
        let bidVelocity = 0;
        if (bids.length >= 2) {
            const sortedBids = [...bids].sort((a, b) => {
                const dateA = a.created_at ? new Date(a.created_at) : null;
                const dateB = b.created_at ? new Date(b.created_at) : null;
                if (!dateA || !dateB) return 0;
                return dateA - dateB;
            }).filter(b => b.created_at); // Filter out bids without created_at
            
            if (sortedBids.length >= 2) {
            const timeSpan = (new Date(sortedBids[sortedBids.length - 1].created_at) - new Date(sortedBids[0].created_at)) / 1000 / 60; // minutes
                bidVelocity = timeSpan > 0 ? sortedBids.length / timeSpan : sortedBids.length;
            }
        } else if (bidCount > 0 && lot.last_bid_time && lot.created_at) {
            // Fallback: calculate velocity using lot timestamps if bidHistory is not available
            try {
                const lotCreatedAt = new Date(lot.created_at);
                const lastBidTime = new Date(lot.last_bid_time);
                if (!isNaN(lotCreatedAt.getTime()) && !isNaN(lastBidTime.getTime())) {
                    const timeSpan = (lastBidTime - lotCreatedAt) / 1000 / 60; // minutes
                    bidVelocity = timeSpan > 0 ? bidCount / timeSpan : 0;
                }
            } catch (e) {
                // Invalid dates, leave bidVelocity as 0
                console.warn('Error calculating bid velocity from lot timestamps:', e);
            }
        }

        // Calculate price growth rate
        const priceGrowth = startingPrice > 0 && currentPrice > startingPrice 
            ? ((currentPrice - startingPrice) / startingPrice) * 100 
            : 0;

        // Calculate average bid increment
        let avgIncrement = increment;
        if (bids.length >= 2) {
            const sortedBids = [...bids]
                .filter(b => b.created_at && b.amount) // Filter out invalid bids
                .sort((a, b) => {
                    const dateA = a.created_at ? new Date(a.created_at) : null;
                    const dateB = b.created_at ? new Date(b.created_at) : null;
                    if (!dateA || !dateB) return 0;
                    return dateA - dateB;
                });
            
            if (sortedBids.length >= 2) {
            const increments = [];
            for (let i = 1; i < sortedBids.length; i++) {
                    const incrementValue = parseFloat(sortedBids[i].amount) - parseFloat(sortedBids[i - 1].amount);
                    if (incrementValue > 0) {
                        increments.push(incrementValue);
                    }
            }
            if (increments.length > 0) {
                avgIncrement = increments.reduce((a, b) => a + b, 0) / increments.length;
                }
            }
        }

        // Analyze similar lots for market comparison
        const similarLots = allLots?.filter(l => 
            l.id !== lot.id && 
            l.status === 'closed' && 
            l.category_id === lot.category_id
        ) || [];
        
        const avgFinalPrice = similarLots.length > 0
            ? similarLots.reduce((sum, l) => sum + parseFloat(l.current_price || 0), 0) / similarLots.length
            : null;

        // Predict final price using multiple factors
        let predictedFinalPrice = currentPrice;
        let confidence = 0.5;

        // Strategy 1: Trend analysis
        if (bids.length >= 3) {
            const validBids = bids.filter(b => b.created_at && b.amount);
            if (validBids.length >= 3) {
                const recentBids = [...validBids]
                    .sort((a, b) => {
                        const dateA = new Date(a.created_at);
                        const dateB = new Date(b.created_at);
                        return dateB - dateA; // Descending order (newest first)
                    })
                    .slice(0, 3);
                const recentPrices = recentBids.map(b => parseFloat(b.amount)).filter(p => !isNaN(p));
                if (recentPrices.length >= 2) {
            const trend = recentPrices[0] - recentPrices[recentPrices.length - 1];
            const trendFactor = trend > 0 ? 1.2 : 0.8;
            predictedFinalPrice = currentPrice * trendFactor;
            confidence += 0.1;
                }
            }
        }

        // Strategy 2: Reserve price consideration
        if (reservePrice && currentPrice < reservePrice) {
            const distanceToReserve = (reservePrice - currentPrice) / currentPrice;
            if (distanceToReserve < 0.2) {
                predictedFinalPrice = Math.max(predictedFinalPrice, reservePrice * 1.1);
                confidence += 0.15;
            } else {
                predictedFinalPrice = Math.max(predictedFinalPrice, reservePrice * 0.95);
            }
        }

        // Strategy 3: Market comparison
        if (avgFinalPrice) {
            const marketFactor = currentPrice / avgFinalPrice;
            if (marketFactor < 0.8) {
                predictedFinalPrice = Math.max(predictedFinalPrice, avgFinalPrice * 0.85);
                confidence += 0.1;
            }
        }

        // Strategy 4: Bid velocity analysis
        if (bidVelocity > 2) {
            predictedFinalPrice *= 1.15; // High activity = higher final price
            confidence += 0.1;
        } else if (bidVelocity < 0.5 && bidCount > 5) {
            predictedFinalPrice *= 0.9; // Slowing down
        }

        // Calculate recommended bid amounts
        const minBid = currentPrice + increment;
        const conservativeBid = minBid + (increment * 2);
        const aggressiveBid = minBid + (increment * 5);
        const smartBid = Math.round((predictedFinalPrice * 0.7 + currentPrice * 0.3) / increment) * increment;

        // Determine bidding strategy
        let strategy = 'balanced';
        let strategyTip = 'Bid strategically at key moments';
        
        if (bidVelocity > 3 && bidCount > 10) {
            strategy = 'competitive';
            strategyTip = 'High competition - bid early to establish presence';
        } else if (reservePrice && currentPrice < reservePrice * 0.9) {
            strategy = 'reserve-focused';
            // Only show reserve-specific tip to auction owners, generic tip to others
            strategyTip = currentUser?.id === auctionOwnerId 
                ? 'Below reserve - good opportunity if you value this lot'
                : 'Good bidding opportunity available';
        } else if (bidVelocity < 0.5 && bidCount < 5) {
            strategy = 'patient';
            strategyTip = 'Low activity - wait for better opportunity';
        } else if (currentPrice > startingPrice * 2) {
            strategy = 'premium';
            strategyTip = 'Premium pricing - ensure value matches your budget';
        }

        // Calculate winning probability (simplified)
        const winningProbability = Math.min(confidence * 100, 85);
        
        // Fair value estimate
        const fairValue = reservePrice 
            ? Math.max(reservePrice, startingPrice * 1.5)
            : startingPrice * 1.8;

        return {
            recommendedBids: {
                minimum: minBid,
                conservative: conservativeBid,
                smart: smartBid,
                aggressive: aggressiveBid
            },
            predictedFinalPrice: Math.round(predictedFinalPrice),
            fairValue: Math.round(fairValue),
            winningProbability: Math.round(winningProbability),
            strategy,
            strategyTip,
            insights: {
                bidCount,
                bidVelocity: bidVelocity.toFixed(2),
                priceGrowth: priceGrowth.toFixed(1),
                avgIncrement: Math.round(avgIncrement),
                isHot: bidVelocity > 2,
                isBelowReserve: reservePrice ? currentPrice < reservePrice : false,
                reserveDistance: reservePrice ? ((reservePrice - currentPrice) / reservePrice * 100).toFixed(1) : null
            }
        };
    };

    if (!lot || lot.status === 'closed' || lot.status === 'cancelled') {
        return null;
    }

    if (loading) {
        return (
            <Card 
                style={{ 
                    marginBottom: 16,
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%)',
                    border: '1px solid #91d5ff'
                }}
            >
                <Space>
                    <Spin size="small" />
                    <Text type="secondary">Analyzing bidding patterns...</Text>
                </Space>
            </Card>
        );
    }

    if (!recommendations) {
        return null;
    }

    const { recommendedBids, predictedFinalPrice, fairValue, winningProbability, strategy, strategyTip, insights } = recommendations;

    const getStrategyColor = () => {
        switch (strategy) {
            case 'competitive': return '#ff4d4f';
            case 'reserve-focused': return '#faad14';
            case 'patient': return '#52c41a';
            case 'premium': return '#722ed1';
            default: return '#1890ff';
        }
    };

    return (
        <Card 
            title={
                <Space>
                    <RobotOutlined style={{ color: '#1890ff' }} />
                    <span>AI Bid Recommendations</span>
                </Space>
            }
            style={{ 
                marginBottom: 16,
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%)',
                border: '1px solid #91d5ff'
            }}
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* Strategy Tag */}
                <div>
                    <Tag color={getStrategyColor()} style={{ fontSize: '12px', padding: '4px 8px' }}>
                        {strategy.charAt(0).toUpperCase() + strategy.slice(1)} Strategy
                    </Tag>
                    <Text type="secondary" style={{ fontSize: '12px', marginLeft: 8 }}>
                        {strategyTip}
                    </Text>
                </div>

                {/* Recommended Bids */}
                <div>
                    <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
                        <BulbOutlined style={{ marginRight: 4 }} />
                        Recommended Bid Amounts
                    </Text>
                    <Row gutter={[8, 8]}>
                        <Col span={12}>
                            <Card size="small" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                                <Text type="secondary" style={{ fontSize: '11px' }}>Smart Bid</Text>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                                    {formatCurrency(recommendedBids.smart)}
                                </div>
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card size="small" style={{ background: '#fff7e6', border: '1px solid #ffd591' }}>
                                <Text type="secondary" style={{ fontSize: '11px' }}>Conservative</Text>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>
                                    {formatCurrency(recommendedBids.conservative)}
                                </div>
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card size="small" style={{ background: '#fff1f0', border: '1px solid #ffccc7' }}>
                                <Text type="secondary" style={{ fontSize: '11px' }}>Aggressive</Text>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#ff4d4f' }}>
                                    {formatCurrency(recommendedBids.aggressive)}
                                </div>
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card size="small" style={{ background: '#f0f0f0', border: '1px solid #d9d9d9' }}>
                                <Text type="secondary" style={{ fontSize: '11px' }}>Minimum</Text>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#595959' }}>
                                    {formatCurrency(recommendedBids.minimum)}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>

                {/* Market Analysis */}
                <Divider style={{ margin: '12px 0' }} />
                <Row gutter={16}>
                    <Col span={12}>
                        <Statistic
                            title="Predicted Final Price"
                            value={formatCurrency(predictedFinalPrice)}
                            valueStyle={{ color: '#1890ff', fontSize: '16px' }}
                            prefix={<RiseOutlined />}
                        />
                    </Col>
                    <Col span={12}>
                        <Statistic
                            title="Fair Value Estimate"
                            value={formatCurrency(fairValue)}
                            valueStyle={{ color: '#52c41a', fontSize: '16px' }}
                            prefix={<DollarOutlined />}
                        />
                    </Col>
                </Row>

                {/* Winning Probability */}
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Text strong style={{ fontSize: '12px' }}>Winning Probability</Text>
                        <Text strong style={{ fontSize: '12px', color: winningProbability > 60 ? '#52c41a' : winningProbability > 40 ? '#faad14' : '#ff4d4f' }}>
                            {winningProbability}%
                        </Text>
                    </div>
                    <Progress 
                        percent={winningProbability} 
                        strokeColor={winningProbability > 60 ? '#52c41a' : winningProbability > 40 ? '#faad14' : '#ff4d4f'}
                        size="small"
                    />
                </div>

                {/* Insights */}
                <Divider style={{ margin: '12px 0' }} />
                <div>
                    <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>
                        <BarChartOutlined style={{ marginRight: 4 }} />
                        Market Insights
                    </Text>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Text style={{ fontSize: '11px' }}>
                            • Total Bids: <strong>{insights.bidCount}</strong>
                            {insights.isHot && <Tag color="red" size="small" style={{ marginLeft: 4 }}>Hot</Tag>}
                        </Text>
                        <Text style={{ fontSize: '11px' }}>
                            • Price Growth: <strong>{insights.priceGrowth}%</strong> from starting price
                        </Text>
                        {insights.isBelowReserve && currentUser?.id === auctionOwnerId && (
                            <Text style={{ fontSize: '11px', color: '#faad14' }}>
                                • <strong>{insights.reserveDistance}%</strong> below reserve price
                            </Text>
                        )}
                    </Space>
                </div>
            </Space>
        </Card>
    );
};

export default AIPriceRecommendation;

